import React, { useEffect, useState } from "react";
import {
  ComputerDesktopIcon,
  MapPinIcon,
  CheckCircleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Screen } from "../../schemas/screenSchema";
import Loader from "../common/Loader";
import { useSchedular } from "../../hooks/useSchedular";
import { useCreativeDownloadStatusSubscription } from "../../hooks/useCreativeDownloadStatusSubscription";
import NowPlayingPreview from "../NowPlayingPreview";
import ScreenTimeline from "../subComponents/ScreenTimeline";
import Modal from "../common/Modal";
import PausedBadge from "../common/PausedBadge";
import SupersededBadge from "../common/SupersededBadge";
import { toDisplayDateInput } from "../../utils/dateUtils";

type ScreenDetailsTab = "overview" | "timeline";

const TABS: Array<{ label: string; value: ScreenDetailsTab }> = [
  { label: "Overview & Schedule", value: "overview" },
  { label: "Timeline", value: "timeline" },
];

interface ScreenDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  device?: Screen | null;
}

export default function ScreenDetailsModal({
  isOpen,
  onClose,
  device,
}: ScreenDetailsModalProps) {
  const {
    useScreenSchedules,
    deleteAssignment,
    deleteAssignmentForDate,
    deleteAllSchedulesForScreen,
  } = useSchedular();

  const [selectedDate, setSelectedDate] = useState<string>(() =>
    toDisplayDateInput(),
  );

  const [activeTab, setActiveTab] = useState<ScreenDetailsTab>("overview");

  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    scheduleId: string;
  }>({ isOpen: false, scheduleId: "" });

  const [expandedMainPlaylists, setExpandedMainPlaylists] = useState<
    Set<string>
  >(new Set());
  const [expandedSubPlaylists, setExpandedSubPlaylists] = useState<Set<string>>(
    new Set(),
  );
  const [expandedClientPlaylists, setExpandedClientPlaylists] = useState<
    Set<string>
  >(new Set());

  const {
    data: schedulesData,
    isLoading: isLoadingSchedules,
    isFetching: isFetchingSchedules,
    refetch: refetchSchedules,
  } = useScreenSchedules(device?.id as string, selectedDate);

  useEffect(() => {
    if (isOpen && device?.id) {
      refetchSchedules();
    }
  }, [isOpen, device?.id, selectedDate, refetchSchedules]);

  // Reopening the modal (or switching screens) should land on the default tab
  // rather than whatever was last viewed for a different screen.
  useEffect(() => {
    setActiveTab("overview");
  }, [isOpen, device?.id]);

  useCreativeDownloadStatusSubscription(device?.id, isOpen);

  const toggleMainPlaylist = (slotIndex: number, mainIndex: number) => {
    const key = `${slotIndex}-${mainIndex}`;
    const newSet = new Set(expandedMainPlaylists);
    if (newSet.has(key)) {
      newSet.delete(key);
      // Also close any expanded sub-playlists
      const newSubSet = new Set(expandedSubPlaylists);
      Array.from(expandedSubPlaylists).forEach((subKey) => {
        if (subKey.startsWith(key)) {
          newSubSet.delete(subKey);
        }
      });
      setExpandedSubPlaylists(newSubSet);
    } else {
      newSet.add(key);
    }
    setExpandedMainPlaylists(newSet);
  };

  const toggleSubPlaylist = (
    slotIndex: number,
    mainIndex: number,
    subIndex: number,
  ) => {
    const key = `${slotIndex}-${mainIndex}-${subIndex}`;
    const newSet = new Set(expandedSubPlaylists);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedSubPlaylists(newSet);
  };

  const toggleClientPlaylist = (scheduleIndex: number) => {
    const key = `${scheduleIndex}`;
    const newSet = new Set(expandedClientPlaylists);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedClientPlaylists(newSet);
  };

  const handleDeleteClick = (scheduleId: string) => {
    setDeleteConfirm({ isOpen: true, scheduleId });
  };

  const handleDeleteForDate = () => {
    deleteAssignmentForDate({
      id: deleteConfirm.scheduleId,
      date: selectedDate,
    });
    setDeleteConfirm({ isOpen: false, scheduleId: "" });
  };

  const handleDeleteEntire = () => {
    deleteAssignment(deleteConfirm.scheduleId);
    setDeleteConfirm({ isOpen: false, scheduleId: "" });
  };

  const handleDeleteAllForScreen = () => {
    if (device?.id) {
      deleteAllSchedulesForScreen(device.id);
    }
    setDeleteConfirm({ isOpen: false, scheduleId: "" });
  };

  const renderCreativeStatus = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "downloaded":
        return (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
            title="Downloaded"
          >
            <CheckCircleIcon className="w-3.5 h-3.5" />
          </span>
        );
      case "downloading":
        return (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 animate-pulse"
            title="Downloading"
          >
            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
          </span>
        );
      case "pending":
        return (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"
            title="Pending"
          >
            <ExclamationTriangleIcon className="w-3.5 h-3.5" />
          </span>
        );
      case "failed":
        return (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"
            title="Failed"
          >
            <XCircleIcon className="w-3.5 h-3.5" />
          </span>
        );
      default:
        return (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
            title={status || "Unknown"}
          >
            <ClockIcon className="w-3.5 h-3.5" />
          </span>
        );
    }
  };

  if (!device) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Device Overview & Schedule"
        size="full"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 flex-shrink-0">
          <div className="flex border-b-2 border-primary-default">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={`px-6 py-2 rounded-t-lg font-medium transition-colors duration-150 ${
                  activeTab === tab.value
                    ? "text-white bg-primary-default"
                    : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "overview" && (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          )}
        </div>

        {/* Mounted only while selected, so opening the modal doesn't fire the
            timeline query for every screen the user merely glances at. */}
        {activeTab === "timeline" && (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden border rounded-xl p-4 shadow-sm">
            <ScreenTimeline
              screenId={device.id}
              isActive={isOpen}
              screenName={device.screenName}
              city={device.region?.city?.name}
              region={device.region?.name}
            />
          </div>
        )}

        <div
          className={`${
            activeTab === "overview" ? "grid" : "hidden"
          } grid-cols-1 md:grid-cols-3 md:grid-rows-1 gap-6 flex-1 min-h-0`}
        >
          {/* Left Column: Overview + Live Preview */}
          <div className="flex flex-col">
            {/* Overview Section */}
            <div className="border rounded-xl p-4 bg-gray-50 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <ComputerDesktopIcon className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">
                  {device.screenName}
                </span>
              </div>
              <div className="flex items-center gap-1 mb-1">
                <CheckCircleIcon className="w-4 h-4 text-green-600" />
                <span className="text-gray-600 text-xs">{device.tags}</span>
              </div>
              <div className="flex items-center gap-1 mb-1">
                <MapPinIcon className="w-4 h-4 text-gray-600" />
                <span className="text-gray-600 text-xs">
                  {device.address || "Address not available"}
                </span>
              </div>
              <div className="text-gray-600 text-xs mb-1">
                Region:{" "}
                <span className="font-semibold">
                  {device.region?.name || "N/A"}
                </span>
              </div>
              <div className="text-gray-600 text-xs">
                City:{" "}
                <span className="font-semibold">
                  {device.region?.city?.name || "N/A"}
                </span>
              </div>
            </div>

            {/* Live Preview */}
            <NowPlayingPreview
              screenId={device.id}
              screenStatus={device.status ?? false}
              isOpen={isOpen}
            />
          </div>

          {/* Schedule Section */}
          <div className="md:col-span-2">
            <div className="border rounded-xl p-4 shadow-sm flex flex-col h-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Schedule</h3>

              {isLoadingSchedules || isFetchingSchedules ? (
                <div className="flex items-center justify-center py-8">
                  <Loader size="small" />
                </div>
              ) : schedulesData && schedulesData.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
                  {/* Left Section: Scheduled Playlists */}
                  <div className="flex flex-col border rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-primary-default text-white font-bold py-2 px-4 grid grid-cols-12 gap-2 flex-shrink-0">
                      <div className="col-span-2">SLNO</div>
                      <div className="col-span-10">NAME</div>
                    </div>

                    {/* Scrollable Schedule Entries */}
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <div className="space-y-2">
                        {(() => {
                          let globalSerialNumber = 0;
                          return schedulesData.map((screenSchedule) =>
                            screenSchedule.schedule.slots
                              .slice()
                              .sort((a, b) =>
                                a.startTime.localeCompare(b.startTime),
                              )
                              .map((slot, slotIndex) => (
                                <div key={slotIndex}>
                                  {/* Time Slot Header */}
                                  <div className="bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                                    {slot.startTime} - {slot.endTime}
                                  </div>
                                  {slot.playlist
                                    .slice() // Create a copy to avoid mutating the original array
                                    .sort((a, b) => {
                                      // Sort by playOrder, handling null/undefined values
                                      const orderA = a.playOrder ?? 999999;
                                      const orderB = b.playOrder ?? 999999;
                                      return orderA - orderB;
                                    })
                                    .map((mainPlaylist, mainIndex) => {
                                      globalSerialNumber++;
                                      const mainKey = `${slotIndex}-${mainIndex}`;
                                      const isMainExpanded =
                                        expandedMainPlaylists.has(mainKey);

                                      return (
                                        <div key={`${slotIndex}-${mainIndex}`}>
                                          {/* Main Playlist Row */}
                                          <div
                                            className={`grid grid-cols-12 gap-2 py-3 px-4 border-b border-gray-200 hover:bg-gray-100 transition-colors ${
                                              mainPlaylist.isSuperseded
                                                ? "opacity-60"
                                                : ""
                                            }`}
                                          >
                                            <div className="col-span-11">
                                              <div className="flex items-center gap-2">
                                                <button
                                                  onClick={() =>
                                                    toggleMainPlaylist(
                                                      slotIndex,
                                                      mainIndex,
                                                    )
                                                  }
                                                  className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                                                >
                                                  {isMainExpanded ? (
                                                    <ChevronDownIcon className="w-4 h-4" />
                                                  ) : (
                                                    <ChevronRightIcon className="w-4 h-4" />
                                                  )}
                                                  <CheckCircleIcon
                                                    className={`w-5 h-5 ${
                                                      mainPlaylist.isSuperseded
                                                        ? "text-gray-400"
                                                        : "text-green-500"
                                                    }`}
                                                  />
                                                  <span className="text-blue-600 font-medium underline cursor-pointer">
                                                    {mainPlaylist.playlistName}
                                                  </span>
                                                </button>
                                                {(mainPlaylist.isSuperseded ||
                                                  !!mainPlaylist
                                                    .supersededWindows
                                                    ?.length) && (
                                                  <SupersededBadge
                                                    windows={
                                                      mainPlaylist.isSuperseded
                                                        ? undefined
                                                        : mainPlaylist.supersededWindows
                                                    }
                                                  />
                                                )}
                                              </div>
                                              {/* <div className="text-sm text-gray-500 mt-1 ml-7">
                                                {slot.startTime} -{" "}
                                                {slot.endTime}
                                              </div> */}
                                            </div>
                                            <div className="col-span-1 flex items-center justify-center">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (mainPlaylist.id) {
                                                    handleDeleteClick(
                                                      mainPlaylist.id,
                                                    );
                                                  }
                                                }}
                                                className="text-red-500 hover:text-red-700 transition-colors"
                                                title="Delete schedule"
                                              >
                                                <TrashIcon className="w-5 h-5" />
                                              </button>
                                            </div>
                                          </div>

                                          {/* Sub-Playlists (Expanded) */}
                                          {isMainExpanded && (
                                            <div className="bg-gray-50">
                                              {mainPlaylist.playlist
                                                .slice()
                                                .sort((a, b) => {
                                                  const orderA =
                                                    a.playOrder ?? 999999;
                                                  const orderB =
                                                    b.playOrder ?? 999999;
                                                  return orderA - orderB;
                                                })
                                                .map(
                                                  (subPlaylist, subIndex) => {
                                                    const subKey = `${slotIndex}-${mainIndex}-${subIndex}`;
                                                    const isSubExpanded =
                                                      expandedSubPlaylists.has(
                                                        subKey,
                                                      );

                                                    return (
                                                      <div
                                                        key={subIndex}
                                                        className="border-gray-300"
                                                      >
                                                        {/* Sub-Playlist Row */}
                                                        <div
                                                          className="grid grid-cols-12 gap-2 py-2 px-4 hover:bg-gray-100 transition-colors cursor-pointer"
                                                          onClick={() =>
                                                            toggleSubPlaylist(
                                                              slotIndex,
                                                              mainIndex,
                                                              subIndex,
                                                            )
                                                          }
                                                        >
                                                          <div className="col-span-1"></div>
                                                          <div className="col-span-10">
                                                            <div className="flex items-center gap-2">
                                                              {isSubExpanded ? (
                                                                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                                                              ) : (
                                                                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                                                              )}
                                                              <span className="text-gray-700 font-medium">
                                                                {
                                                                  subPlaylist.playlistName
                                                                }
                                                              </span>
                                                            </div>
                                                          </div>
                                                        </div>

                                                        {/* Creatives (Expanded) */}
                                                        {isSubExpanded && (
                                                          <div>
                                                            {subPlaylist.playlist
                                                              .slice()
                                                              .sort((a, b) => {
                                                                const orderA =
                                                                  a.playOrder ??
                                                                  999999;
                                                                const orderB =
                                                                  b.playOrder ??
                                                                  999999;
                                                                return (
                                                                  orderA -
                                                                  orderB
                                                                );
                                                              })
                                                              .map(
                                                                (
                                                                  creative,
                                                                  creativeIndex,
                                                                ) => (
                                                                  <div
                                                                    key={
                                                                      creativeIndex
                                                                    }
                                                                    className="grid grid-cols-12 gap-2 py-2 px-4 border-b border-gray-100"
                                                                  >
                                                                    <div className="col-span-2"></div>
                                                                    <div
  className={`col-span-10 flex items-center gap-2 ${
    creative.isPaused ? "opacity-60" : ""
  }`}
>
  {creative.isPaused && <PausedBadge className="order-last" />}
                                                                      {renderCreativeStatus(
                                                                        creative.status,
                                                                      )}
                                                                      <span className="text-sm text-gray-700">
                                                                        {
                                                                          creative.creativeName
                                                                        }
                                                                      </span>
                                                                      {creative.customerName && (
                                                                        <span className="text-xs text-gray-500">
                                                                          (
                                                                          {
                                                                            creative.customerName
                                                                          }
                                                                          )
                                                                        </span>
                                                                      )}
                                                                    </div>
                                                                  </div>
                                                                ),
                                                              )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    );
                                                  },
                                                )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )),
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Right Section: Restaurant Playlist */}
                  <div className="flex flex-col border rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-primary-default text-white font-bold py-2 px-4 grid grid-cols-12 gap-2 flex-shrink-0">
                      <div className="col-span-12">NAME</div>
                    </div>

                    {/* Scrollable Client Playlist Entries */}
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <div className="space-y-2">
                        {schedulesData.map((screenSchedule, scheduleIndex) => {
                          if (!screenSchedule.clientPlaylist) return null;

                          const clientKey = `${scheduleIndex}`;
                          const isClientExpanded =
                            expandedClientPlaylists.has(clientKey);

                          return (
                            <div key={scheduleIndex}>
                              {/* Restaurant Playlist Header */}
                              <div className="bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                                Restaurant Playlist
                              </div>
                              {/* Client Playlist Row */}
                              <div
                                className="grid grid-cols-12 gap-2 py-3 px-4 border-b border-gray-200 bg-white hover:bg-gray-100 transition-colors"
                                onClick={() =>
                                  toggleClientPlaylist(scheduleIndex)
                                }
                              >
                                <div className="col-span-2 flex items-center">
                                  <button className="flex items-center gap-2 hover:text-blue-600 transition-colors">
                                    {isClientExpanded ? (
                                      <ChevronDownIcon className="w-4 h-4" />
                                    ) : (
                                      <ChevronRightIcon className="w-4 h-4" />
                                    )}
                                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                  </button>
                                </div>
                                <div className="col-span-10">
                                  <div className="flex items-center gap-2">
                                    <span className="text-blue-600 font-medium underline cursor-pointer">
                                      {screenSchedule.clientPlaylist.name}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Client Playlist Creatives (Expanded) */}
                              {isClientExpanded &&
                                screenSchedule.clientPlaylist.playlist &&
                                screenSchedule.clientPlaylist.playlist.length >
                                  0 && (
                                  <div className="bg-gray-50">
                                    {screenSchedule.clientPlaylist.playlist
                                      .slice()
                                      .sort((a, b) => {
                                        const orderA = a.playOrder ?? 999999;
                                        const orderB = b.playOrder ?? 999999;
                                        return orderA - orderB;
                                      })
                                      .map((creative, creativeIndex) => (
                                        <div
                                          key={creativeIndex}
                                          className="border-gray-300 bg-white"
                                        >
                                          <div className="grid grid-cols-12 gap-2 py-2 px-4">
                                            <div className="col-span-1"></div>
                                            <div
  className={`col-span-10 flex items-center gap-2 ${
    creative.isPaused ? "opacity-60" : ""
  }`}
>
  {creative.isPaused && <PausedBadge className="order-last" />}
                                              {renderCreativeStatus(
                                                creative.status,
                                              )}
                                              <span className="text-sm text-gray-700">
                                                {creative.creativeName}
                                              </span>
                                              {creative.customerName && (
                                                <span className="text-xs text-gray-500">
                                                  ({creative.customerName})
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}
                            </div>
                          );
                        })}
                        {schedulesData.every(
                          (schedule) => !schedule.clientPlaylist,
                        ) && (
                          <div className="text-center py-8 text-gray-500 text-sm">
                            No restaurant playlist assigned
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ClockIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No schedules found for this date</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {/* <div className="flex justify-end mt-6 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700"
          >
            Close
          </button>
        </div> */}
      </Modal>

      {/* Delete Confirmation Popup */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() =>
                setDeleteConfirm({ isOpen: false, scheduleId: "" })
              }
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-800">
                Delete Schedule
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                How would you like to delete this schedule?
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleDeleteForDate}
                className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
              >
                <CalendarDaysIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-800">
                    Delete for {selectedDate} only
                  </div>
                  <div className="text-xs text-gray-500">
                    Removes the schedule only for the selected date on this
                    screen
                  </div>
                </div>
              </button>

              <button
                onClick={handleDeleteEntire}
                className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors text-left"
              >
                <TrashIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-800">
                    Delete entire schedule for this screen
                  </div>
                  <div className="text-xs text-gray-500">
                    Removes the schedule completely from this screen (other
                    screens are not affected)
                  </div>
                </div>
              </button>

              <button
                onClick={handleDeleteAllForScreen}
                className="w-full flex items-center gap-3 px-4 py-3 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-400 transition-colors text-left"
              >
                <ExclamationTriangleIcon className="w-5 h-5 text-red-700 flex-shrink-0" />
                <div>
                  <div className="font-medium text-red-700">
                    Delete all schedules for this screen
                  </div>
                  <div className="text-xs text-gray-500">
                    Removes every schedule from this screen
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() =>
                  setDeleteConfirm({ isOpen: false, scheduleId: "" })
                }
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
